// js/_shared/_toast.js
/* =============================================================================
   SHARED TOAST NOTIFICATIONS
   Path: /js/_shared/_toast.js
   
   Purpose: Unified toast notification system for user feedback.
            Provides success, error, warning, and info messages with auto-dismiss.
   
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   
   Namespace: window.AppUtils.Toast
   Global Alias: window.Toast (for convenience)
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.Toast = (function () {
    'use strict';

    /* =========================================================================
       PRIVATE VARIABLES
       ========================================================================= */

    var _container = null;
    var _toastId = 0;
    var _stylesInjected = false;

    var _config = {
        defaultDuration: 4000,
        errorDuration: 5000,
        position: { bottom: '20px', right: '20px' },
        colors: {
            success: { bg: 'var(--r4o-success)', icon: '✓' },
            error: { bg: 'var(--r4o-danger)', icon: '✗' },
            warning: { bg: 'var(--r4o-warning)', icon: '⚠' },
            info: { bg: 'var(--r4o-info)', icon: 'ℹ' }
        }
    };

    /* =========================================================================
       STYLES
       ========================================================================= */

    var _styles = [
        '.toast-container {',
        '    position: fixed;',
        '    bottom: 20px;',
        '    right: 20px;',
        '    z-index: 10000;',
        '    display: flex;',
        '    flex-direction: column;',
        '    gap: 10px;',
        '    pointer-events: none;',
        '}',
        '.toast-item {',
        '    display: flex;',
        '    align-items: center;',
        '    gap: 12px;',
        '    padding: 14px 18px;',
        '    border-radius: 8px;',
        '    color: var(--r4o-white);',
        '    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;',
        '    font-size: 14px;',
        '    font-weight: 500;',
        '    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);',
        '    pointer-events: auto;',
        '    animation: toastSlideUp 0.3s ease-out;',
        '    max-width: 400px;',
        '}',
        '.toast-item.is-hiding {',
        '    animation: toastSlideOut 0.3s ease-in forwards;',
        '}',
        '.toast-item--success { background: var(--r4o-success); }',
        '.toast-item--error { background: var(--r4o-danger); }',
        '.toast-item--warning { background: var(--r4o-warning); }',
        '.toast-item--info { background: var(--r4o-info); }',
        '.toast-icon {',
        '    font-size: 18px;',
        '    flex-shrink: 0;',
        '}',
        '.toast-message {',
        '    flex: 1;',
        '    line-height: 1.4;',
        '}',
        '.toast-close {',
        '    background: none;',
        '    border: none;',
        '    color: inherit;',
        '    font-size: 20px;',
        '    cursor: pointer;',
        '    opacity: 0.7;',
        '    padding: 0 0 0 8px;',
        '    line-height: 1;',
        '}',
        '.toast-close:hover {',
        '    opacity: 1;',
        '}',
        '@keyframes toastSlideUp {',
        '    from {',
        '        transform: translateY(20px);',
        '        opacity: 0;',
        '    }',
        '    to {',
        '        transform: translateY(0);',
        '        opacity: 1;',
        '    }',
        '}',
        '@keyframes toastSlideOut {',
        '    from {',
        '        transform: translateX(0);',
        '        opacity: 1;',
        '    }',
        '    to {',
        '        transform: translateX(100%);',
        '        opacity: 0;',
        '    }',
        '}'
    ].join('\n');

    /* =========================================================================
       INITIALIZATION
       ========================================================================= */

    function _injectStyles() {
        if (_stylesInjected) return;

        var style = document.createElement('style');
        style.id = 'toast-styles';
        style.textContent = _styles;
        document.head.appendChild(style);
        _stylesInjected = true;
    }

    function _ensureContainer() {
        if (_container && document.body.contains(_container)) {
            return _container;
        }

        _injectStyles();

        _container = document.createElement('div');
        _container.id = 'toast-container';
        _container.className = 'toast-container';
        _container.setAttribute('aria-live', 'polite');
        document.body.appendChild(_container);

        return _container;
    }

    /* =========================================================================
       CORE FUNCTIONS
       ========================================================================= */

    /**
     * Escape HTML to prevent XSS
     * @param {string} str - String to escape
     * @returns {string}
     */
    function _escapeHtml(str) {
        var div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
    }

    /**
     * Show a toast notification
     * @param {string} message - Message to display
     * @param {Object} [options] - Options
     * @param {string} [options.type='info'] - Toast type: success, error, warning, info
     * @param {number} [options.duration] - Auto-dismiss in ms (0 = no auto-dismiss)
     * @param {boolean} [options.dismissible=true] - Show close button
     * @returns {string} Toast ID for programmatic dismissal
     */
    function show(message, options) {
        options = options || {};
        var type = options.type || 'info';
        var duration = options.duration !== undefined ? options.duration : _config.defaultDuration;
        var dismissible = options.dismissible !== false;

        var container = _ensureContainer();
        var id = 'toast-' + (++_toastId);
        var colorConfig = _config.colors[type] || _config.colors.info;

        var toast = document.createElement('div');
        toast.id = id;
        toast.className = 'toast-item toast-item--' + type;
        toast.setAttribute('role', 'alert');

        var html = '<span class="toast-icon">' + colorConfig.icon + '</span>';
        html += '<span class="toast-message">' + _escapeHtml(message) + '</span>';
        if (dismissible) {
            html += '<button class="toast-close" aria-label="Dismiss">×</button>';
        }

        toast.innerHTML = html;
        container.appendChild(toast);

        // Close button handler
        var closeBtn = toast.querySelector('.toast-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', function () {
                dismiss(id);
            });
        }

        // Auto-dismiss
        if (duration > 0) {
            setTimeout(function () {
                dismiss(id);
            }, duration);
        }

        return id;
    }

    /**
     * Dismiss a specific toast
     * @param {string} toastId - Toast ID to dismiss
     */
    function dismiss(toastId) {
        var toast = document.getElementById(toastId);
        if (!toast) return;

        toast.classList.add('is-hiding');

        // Remove after animation
        setTimeout(function () {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    }

    /**
     * Dismiss all visible toasts
     */
    function dismissAll() {
        if (!_container) return;

        var toasts = _container.querySelectorAll('.toast-item');
        toasts.forEach(function (toast) {
            dismiss(toast.id);
        });
    }

    /* =========================================================================
       CONVENIENCE METHODS
       ========================================================================= */

    /**
     * Show success toast
     * @param {string} message
     * @param {number} [duration]
     * @returns {string} Toast ID
     */
    function success(message, duration) {
        return show(message, {
            type: 'success',
            duration: duration !== undefined ? duration : _config.defaultDuration
        });
    }

    /**
     * Show error toast (longer default duration)
     * @param {string} message
     * @param {number} [duration]
     * @returns {string} Toast ID
     */
    function error(message, duration) {
        return show(message, {
            type: 'error',
            duration: duration !== undefined ? duration : _config.errorDuration
        });
    }

    /**
     * Show warning toast
     * @param {string} message
     * @param {number} [duration]
     * @returns {string} Toast ID
     */
    function warning(message, duration) {
        return show(message, {
            type: 'warning',
            duration: duration !== undefined ? duration : _config.defaultDuration
        });
    }

    /**
     * Show info toast
     * @param {string} message
     * @param {number} [duration]
     * @returns {string} Toast ID
     */
    function info(message, duration) {
        return show(message, {
            type: 'info',
            duration: duration !== undefined ? duration : _config.defaultDuration
        });
    }

    /**
     * Show loading toast (no auto-dismiss)
     * @param {string} message
     * @returns {string} Toast ID - must be dismissed manually
     */
    function showLoading(message) {
        return show(message, {
            type: 'info',
            duration: 0,
            dismissible: false
        });
    }

    /**
     * Update default configuration
     * @param {Object} config - Configuration options
     */
    function configure(config) {
        if (!config) return;

        if (config.defaultDuration !== undefined) {
            _config.defaultDuration = config.defaultDuration;
        }
        if (config.errorDuration !== undefined) {
            _config.errorDuration = config.errorDuration;
        }
        if (config.position) {
            _config.position = Object.assign({}, _config.position, config.position);
        }
        if (config.colors) {
            Object.keys(config.colors).forEach(function (type) {
                if (_config.colors[type]) {
                    _config.colors[type] = Object.assign({}, _config.colors[type], config.colors[type]);
                }
            });
        }
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    return {
        // Core
        show: show,
        dismiss: dismiss,
        dismissAll: dismissAll,

        // Convenience
        success: success,
        error: error,
        warning: warning,
        info: info,
        showLoading: showLoading,

        // Configuration
        configure: configure
    };

})();

/* =============================================================================
   GLOBAL ALIAS
   ========================================================================== */

window.Toast = window.AppUtils.Toast;
